import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Send, Plus, Trash2, Loader2, MessageSquare, User, Sparkles, Info, ArrowLeft, ArrowRight,
  Church, HeartHandshake, Users, Shield, Store, HardHat, UtensilsCrossed, BookOpen, Target, GraduationCap, Crown, Wand2,
  Rocket, FileText, PenLine, LayoutTemplate, CheckCircle2, MinusCircle, Zap, Check, Star } from "lucide-react";
import { VISUAL_STYLE_OPTIONS, BRAND_PERSONALITY_OPTIONS, VISUAL_PREF_OPTIONS, MOOD_BY_INDUSTRY } from "@/lib/designDna";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Renders assistant replies as clean, professional formatted text (no raw ## or ** markers).
const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h3 className="font-heading text-base text-white mt-3 mb-1.5 first:mt-0" {...p} />,
  h2: ({ node, ...p }) => <h3 className="font-heading text-base text-white mt-3 mb-1.5 first:mt-0" {...p} />,
  h3: ({ node, ...p }) => <h4 className="font-semibold text-white mt-3 mb-1 first:mt-0" {...p} />,
  p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-white" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
  a: ({ node, ...p }) => <a className="text-gold underline hover:text-gold-light" target="_blank" rel="noopener noreferrer" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-gold/50 pl-3 my-2 text-gray-300 italic" {...p} />,
  code: ({ node, inline, ...p }) => inline
    ? <code className="bg-white/10 px-1 py-0.5 rounded text-[0.85em]" {...p} />
    : <code className="block bg-black/30 p-2 rounded my-2 overflow-x-auto text-[0.85em]" {...p} />,
  hr: () => <hr className="border-white/10 my-3" />,
};

const ACTION_ICON = { start_project: Rocket, proposal: FileText, content_studio: PenLine, website_builder: LayoutTemplate };

const INDUSTRIES = [
  { id: "church", label: "Church", icon: Church, chips: ["Write a welcoming homepage headline for our church", "Draft service times & location copy", "Ideas for a sermons/media page", "Write a call-to-action for online giving"] },
  { id: "nonprofit", label: "Nonprofit", icon: HeartHandshake, chips: ["Write a mission statement for our nonprofit", "Draft a compelling donation call-to-action", "Ideas for a volunteer signup page", "Write an impact/results section"] },
  { id: "small-business", label: "Small Business", icon: Store, chips: ["Write a homepage headline for my local business", "Draft an About section that builds trust", "Suggest 5 local SEO keywords", "Write a strong 'Book Now' call-to-action"] },
  { id: "construction", label: "Construction", icon: HardHat, chips: ["Write a headline for my contracting business", "Draft copy that highlights licensing & insurance", "Ideas for a project portfolio page", "Write a 'Request a Free Estimate' CTA"] },
  { id: "restaurant", label: "Restaurant", icon: UtensilsCrossed, chips: ["Write a mouth-watering homepage headline", "Draft a short 'Our Story' section", "Ideas for a menu page layout", "Write copy for online ordering/reservations"] },
  { id: "author", label: "Author", icon: BookOpen, chips: ["Write a headline for my author website", "Draft an author bio", "Write newsletter signup copy", "Ideas for a books/works page"] },
  { id: "coach", label: "Coach / Consultant", icon: Target, chips: ["Write a headline that sells my coaching offer", "Draft a testimonials section", "Write a 'Book a Free Call' CTA", "Outline my services page"] },
  { id: "course", label: "Online Course", icon: GraduationCap, chips: ["Write a headline for my online course", "Draft learning-outcome bullet points", "Write an enrollment call-to-action", "Ideas for a curriculum section"] },
  { id: "membership", label: "Membership Site", icon: Crown, chips: ["Write a headline for my membership site", "Draft member benefit bullet points", "Write copy for pricing tiers", "Write a strong 'Join Now' CTA"] },
  { id: "ministry", label: "Ministry", icon: Users, chips: ["Write a headline for our ministry", "Draft an outreach/programs section", "Write a donation call-to-action", "Ideas for a testimonies page"] },
  { id: "veteran", label: "Veteran Org", icon: Shield, chips: ["Write a respectful headline for our veteran organization", "Draft a 'How we help' section", "Write a resources page intro", "Write a 'Get Support' call-to-action"] },
];

const CUSTOM_INDUSTRY = {
  id: "custom", label: "Other / Custom Business", icon: Wand2,
  chips: ["Write a homepage headline for my business", "Draft an About section that builds trust", "Suggest SEO keywords for my niche", "Write a strong primary call-to-action"],
};

// Guided quick-setup option sets — labels are aligned with the assessment form's own
// option lists (base + industry add-ons) so selections highlight correctly once prefilled.
const QUICK_SETUP = {
  church: { industryLabel: "Church",
    goals: ["Build credibility", "Grow email list", "Accept donations", "Share information", "Book appointments"],
    pages: ["Home", "About", "Events", "Ministries", "Sermons", "Give", "Prayer Requests", "Bible Study", "Contact"],
    features: ["Online Sermons (video + audio)", "Livestream Integration", "Online Giving", "Prayer Request Form", "Events Calendar", "Ministry Pages", "Newsletter signup", "Contact form"] },
  nonprofit: { industryLabel: "Nonprofit",
    goals: ["Build credibility", "Grow email list", "Accept donations", "Share information", "Raise donations", "Recruit volunteers"],
    pages: ["Home", "About", "Donate", "Programs", "Volunteer", "Impact", "Events", "Contact"],
    features: ["Online Donations", "Recurring Giving", "Volunteer Signup", "Event Registration", "Impact Stories", "Donor Portal", "Newsletter signup", "Contact form"] },
  "small-business": { industryLabel: "Small Business",
    goals: ["Generate leads", "Sell products", "Build credibility", "Grow email list", "Book appointments"],
    pages: ["Home", "About", "Services", "Contact", "Pricing", "FAQ", "Gallery", "Blog"],
    features: ["Contact form", "Online booking", "E-commerce", "Newsletter signup", "Photo gallery", "Live chat", "SEO setup", "Payment / donations"] },
  construction: { industryLabel: "Construction",
    goals: ["Generate leads", "Build credibility", "Showcase portfolio", "Get estimate requests", "Showcase past work"],
    pages: ["Home", "About", "Services", "Projects / Portfolio", "Service Areas", "Get a Quote", "Reviews", "Contact"],
    features: ["Free Estimate Form", "Project Gallery / Before & After", "Service Area Map", "Online Booking", "Reviews / Testimonials", "Financing Options", "Contact form", "Photo gallery"] },
  restaurant: { industryLabel: "Restaurant",
    goals: ["Build credibility", "Grow email list", "Share information", "Take online orders", "Grow reservations"],
    pages: ["Home", "About", "Menu", "Reservations", "Order Online", "Locations", "Specials", "Contact"],
    features: ["Online Menu", "Online Ordering", "Table Reservations", "Delivery Integration", "Reviews / Testimonials", "Events & Specials", "Contact form", "Photo gallery"] },
  author: { industryLabel: "Author",
    goals: ["Build credibility", "Grow email list", "Sell products", "Share information", "Showcase portfolio"],
    pages: ["Home", "About", "Blog", "Contact", "Shop", "Gallery", "Events"],
    features: ["Newsletter signup", "E-commerce", "Blog / CMS", "Contact form", "Photo gallery", "SEO setup"] },
  coach: { industryLabel: "Coaching / Consulting",
    goals: ["Generate leads", "Build credibility", "Grow email list", "Book appointments", "Sell products"],
    pages: ["Home", "About", "Services", "Contact", "Pricing", "Blog", "FAQ"],
    features: ["Online booking", "Contact form", "Newsletter signup", "Payment / donations", "Blog / CMS", "Live chat", "SEO setup"] },
  course: { industryLabel: "Online Course",
    goals: ["Sell products", "Grow email list", "Build credibility", "Generate leads", "Share information"],
    pages: ["Home", "About", "Pricing", "Contact", "FAQ", "Blog"],
    features: ["E-commerce", "Membership area", "Newsletter signup", "Payment / donations", "Video", "Contact form", "SEO setup"] },
  membership: { industryLabel: "Membership Site",
    goals: ["Sell products", "Grow email list", "Build credibility", "Generate leads"],
    pages: ["Home", "About", "Pricing", "Contact", "FAQ", "Blog"],
    features: ["Membership area", "E-commerce", "Newsletter signup", "Payment / donations", "Video", "Contact form"] },
  ministry: { industryLabel: "Ministry",
    goals: ["Build credibility", "Grow email list", "Accept donations", "Share information"],
    pages: ["Home", "About", "Events", "Ministries", "Sermons", "Give", "Prayer Requests", "Contact"],
    features: ["Online Giving", "Prayer Request Form", "Events Calendar", "Ministry Pages", "Online Sermons (video + audio)", "Newsletter signup", "Contact form"] },
  veteran: { industryLabel: "Veteran Organization",
    goals: ["Build credibility", "Accept donations", "Share information", "Grow email list", "Recruit volunteers"],
    pages: ["Home", "About", "Programs", "Donate", "Volunteer", "Impact", "Contact"],
    features: ["Online Donations", "Volunteer Signup", "Impact Stories", "Event Registration", "Newsletter signup", "Contact form"] },
  custom: { industryLabel: "",
    goals: ["Generate leads", "Sell products", "Build credibility", "Grow email list", "Accept donations", "Share information", "Book appointments", "Showcase portfolio"],
    pages: ["Home", "About", "Services", "Contact", "Blog", "Gallery", "FAQ", "Pricing"],
    features: ["Contact form", "Online booking", "E-commerce", "Newsletter signup", "Photo gallery", "Blog / CMS", "Live chat", "SEO setup"] },
};
const QS_BUDGETS = ["Under $1,000", "$1,000 - $2,500", "$2,500 - $5,000", "$5,000 - $10,000", "$10,000+"];
const QS_TIMELINES = ["ASAP (1-2 weeks)", "3-4 weeks", "1-2 months", "Flexible"];

// Smart recommendations — the most-picked options per industry get a "Popular" star so
// first-timers know what to choose. Labels must match the QUICK_SETUP option labels above.
const QS_POPULAR = {
  church: { goals: ["Accept donations", "Build credibility"], pages: ["Sermons", "Give", "Events"], features: ["Online Giving", "Online Sermons (video + audio)", "Events Calendar"] },
  nonprofit: { goals: ["Raise donations", "Recruit volunteers"], pages: ["Donate", "Volunteer", "Impact"], features: ["Online Donations", "Volunteer Signup", "Impact Stories"] },
  "small-business": { goals: ["Generate leads", "Build credibility"], pages: ["Services", "Contact"], features: ["Contact form", "Online booking", "SEO setup"] },
  construction: { goals: ["Get estimate requests", "Showcase past work"], pages: ["Projects / Portfolio", "Get a Quote"], features: ["Free Estimate Form", "Project Gallery / Before & After", "Reviews / Testimonials"] },
  restaurant: { goals: ["Grow reservations", "Take online orders"], pages: ["Menu", "Reservations"], features: ["Online Menu", "Table Reservations", "Online Ordering"] },
  author: { goals: ["Grow email list", "Sell products"], pages: ["Blog", "Shop"], features: ["Newsletter signup", "E-commerce"] },
  coach: { goals: ["Generate leads", "Book appointments"], pages: ["Services", "Pricing"], features: ["Online booking", "Contact form"] },
  course: { goals: ["Sell products", "Grow email list"], pages: ["Pricing"], features: ["E-commerce", "Membership area"] },
  membership: { goals: ["Sell products", "Grow email list"], pages: ["Pricing"], features: ["Membership area", "Payment / donations"] },
  ministry: { goals: ["Accept donations", "Build credibility"], pages: ["Sermons", "Give"], features: ["Online Giving", "Prayer Request Form"] },
  veteran: { goals: ["Accept donations", "Recruit volunteers"], pages: ["Donate", "Programs"], features: ["Online Donations", "Volunteer Signup"] },
  custom: { goals: ["Generate leads"], pages: ["Services", "Contact"], features: ["Contact form"] },
};

// Tiny representative swatch per design vibe so visitors can see the look before picking.
const VIBE_SWATCH = {
  "Warm & Welcoming": "linear-gradient(135deg,#f59e0b,#fb923c)",
  "Modern & Timeless": "linear-gradient(135deg,#0f172a,#38bdf8)",
  "Professional": "linear-gradient(135deg,#1e3a8a,#3b82f6)",
  "Elegant": "linear-gradient(135deg,#4c1d95,#d4af37)",
  "Bold": "linear-gradient(135deg,#111827,#ef4444)",
  "Clean & Minimal": "linear-gradient(135deg,#f8fafc,#cbd5e1)",
  "Luxury": "linear-gradient(135deg,#0a0a0a,#d4af37)",
  "Rustic": "linear-gradient(135deg,#78350f,#b45309)",
  "Traditional": "linear-gradient(135deg,#1e3a5f,#7f1d1d)",
  "Creative": "linear-gradient(135deg,#ec4899,#8b5cf6,#22d3ee)",
};

// Maps a concierge industry to the Design DNA mood key (only these industries have moods).
const CONCIERGE_MOOD_KEY = { church: "church", ministry: "church", nonprofit: "nonprofit", veteran: "nonprofit", construction: "contractor", restaurant: "restaurant" };

// Recommended Design DNA defaults per industry — powers the one-tap "Use recommended setup".
// mood values must exist in MOOD_BY_INDUSTRY for that industry's mood key.
const QS_RECO = {
  church: { vibe: ["Warm & Welcoming", "Traditional"], personality: ["Trustworthy", "Community-Focused"], mood: ["Christ-Centered", "Community Focused", "Family Friendly"], pref: ["Large Photography", "Full-width Sections"] },
  ministry: { vibe: ["Warm & Welcoming", "Traditional"], personality: ["Trustworthy", "Community-Focused"], mood: ["Christ-Centered", "Community Focused", "Family Friendly"], pref: ["Large Photography", "Full-width Sections"] },
  nonprofit: { vibe: ["Warm & Welcoming", "Modern & Timeless"], personality: ["Trustworthy", "Community-Focused"], mood: ["Hopeful", "Compassionate", "Mission Driven"], pref: ["Large Photography", "Lots of White Space"] },
  veteran: { vibe: ["Bold", "Traditional"], personality: ["Trustworthy", "Community-Focused"], mood: ["Hopeful", "Mission Driven", "Community Focused"], pref: ["Large Photography", "Full-width Sections"] },
  construction: { vibe: ["Bold", "Professional"], personality: ["Trustworthy", "Premium"], mood: ["Rugged & Dependable", "Professional", "Trustworthy"], pref: ["Large Photography", "Sharp/Corporate"] },
  restaurant: { vibe: ["Warm & Welcoming", "Bold"], personality: ["Friendly", "Family-Oriented"], mood: ["Inviting", "Cozy", "Family Friendly"], pref: ["Large Photography", "Full-width Sections"] },
  "small-business": { vibe: ["Modern & Timeless", "Professional"], personality: ["Trustworthy", "Friendly"], mood: [], pref: ["Large Photography", "Card Layout"] },
  author: { vibe: ["Elegant", "Clean & Minimal"], personality: ["Inspirational", "Trustworthy"], mood: [], pref: ["Large Photography", "Lots of White Space"] },
  coach: { vibe: ["Modern & Timeless", "Professional"], personality: ["Trustworthy", "Inspirational"], mood: [], pref: ["Large Photography", "Card Layout"] },
  course: { vibe: ["Modern & Timeless", "Bold"], personality: ["Innovative", "Trustworthy"], mood: [], pref: ["Card Layout", "Video Hero"] },
  membership: { vibe: ["Modern & Timeless", "Luxury"], personality: ["Premium", "Innovative"], mood: [], pref: ["Dark Theme", "Card Layout"] },
  custom: { vibe: ["Modern & Timeless", "Professional"], personality: ["Trustworthy", "Friendly"], mood: [], pref: ["Large Photography"] },
};

function getVisitorId() {
  let v = localStorage.getItem("atf_visitor_id");
  if (!v) {
    v = "vis_" + (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
    localStorage.setItem("atf_visitor_id", v);
  }
  return v;
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1.5 items-center" data-testid="assistant-typing">
      {[0, 1, 2].map((i) => (
        <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-gold"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }} />
      ))}
    </span>
  );
}

export const AiAssistant = ({ scope = "public", compact = false, mode: convoMode = null, initialSessionId = null, onSessionChange, onIndustryChange, onQuickSetup, quickSetupBusy = false }) => {
  const vid = useRef(getVisitorId());
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [aiConnected, setAiConnected] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [customContext, setCustomContext] = useState(null);
  const [brief, setBrief] = useState({ businessType: "", industry: "", audience: "", goals: "" });
  const [readiness, setReadiness] = useState(null);
  const [mode, setMode] = useState("quick"); // "quick" | "chat" — only used when onQuickSetup is provided
  const [qs, setQs] = useState({ goals: [], pages: [], features: [], visualStyle: [], brandPersonality: [], mood: [], visualPref: [], budget: "", timeline: "", description: "" });
  const [qsPreview, setQsPreview] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const hasQuickSetup = typeof onQuickSetup === "function";
  useEffect(() => {
    const el = inputRef.current;
    if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 160) + "px"; }
  }, [input]);

  const resetQs = () => { setMode("quick"); setQsPreview(false); setQs({ goals: [], pages: [], features: [], visualStyle: [], brandPersonality: [], mood: [], visualPref: [], budget: "", timeline: "", description: "" }); };
  // Pre-tick the popular picks for an industry so a visitor can confirm in one tap.
  const seedQsPopular = (id) => {
    const pop = QS_POPULAR[id] || {};
    setQs((s) => ({ ...s, goals: [...(pop.goals || [])], pages: [...(pop.pages || [])], features: [...(pop.features || [])] }));
  };
  const toggleQs = (key, val) => setQs((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val] }));
  const setQsField = (key, val) => setQs((s) => ({ ...s, [key]: s[key] === val ? "" : val }));

  const params = () => ({ scope, visitor_id: vid.current });

  const checkReadiness = async (sid) => {
    if (compact || !sid) return;
    try {
      const { data } = await api.post("/assistant/readiness", { session_id: sid, ...params() });
      setReadiness(data);
    } catch { setReadiness(null); }
  };

  const routeFor = (action) => {
    if (scope === "admin") {
      const m = { start_project: "/admin?view=projects", proposal: "/admin?view=proposals",
                  content_studio: "/admin?view=ai", website_builder: "/admin?view=ai" };
      return m[action] || null;
    }
    return currentId ? `/start?from_chat=${currentId}` : "/start";
  };

  const goToRecommendation = () => {
    const rec = readiness?.recommendation;
    if (!rec || rec.action === "continue") return;
    const to = routeFor(rec.action);
    if (to) navigate(to);
  };

  const loadSessions = () =>
    api.get("/assistant/sessions", { params: params() }).then((r) => setSessions(r.data)).catch(() => {});

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, [scope]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const openSession = async (id) => {
    try {
      const { data } = await api.get(`/assistant/sessions/${id}`, { params: params() });
      setCurrentId(id);
      onSessionChange?.(id);
      setMessages(data.messages);
      const sessInd = data.session?.industry;
      if (sessInd === "custom") {
        setIndustry(CUSTOM_INDUSTRY);
        setCustomContext(data.session?.custom_context || null);
      } else {
        const ind = INDUSTRIES.find((x) => x.id === sessInd);
        setIndustry(ind || null);
        setCustomContext(null);
      }
      onIndustryChange?.(sessInd || null);
      const lastAssistant = [...data.messages].reverse().find((m) => m.role === "assistant");
      if (lastAssistant) setAiConnected(lastAssistant.ai_connected);
      setReadiness(null);
      checkReadiness(id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const newChat = () => { setCurrentId(null); onSessionChange?.(null); setMessages([]); setInput(""); setIndustry(null); setCustomContext(null); setBrief({ businessType: "", industry: "", audience: "", goals: "" }); setReadiness(null); onIndustryChange?.(null); resetQs(); };

  // Resume a previous conversation on mount (e.g. reopening the embedded concierge
  // after closing it) so the chat isn't lost. Only runs once for the given id.
  useEffect(() => {
    if (initialSessionId && !currentId) openSession(initialSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);

  const submitBrief = () => {
    const parts = [];
    if (brief.businessType.trim()) parts.push(`Business type: ${brief.businessType.trim()}`);
    if (brief.industry.trim()) parts.push(`Industry: ${brief.industry.trim()}`);
    if (brief.audience.trim()) parts.push(`Target audience: ${brief.audience.trim()}`);
    if (brief.goals.trim()) parts.push(`Goals: ${brief.goals.trim()}`);
    if (parts.length === 0) { toast.error("Tell me a little about your business first."); return; }
    setCustomContext(parts.join(". ") + ".");
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { id: "tmp-" + Date.now(), role: "user", content }]);
    try {
      const { data } = await api.post("/assistant/chat", { content, session_id: currentId, industry: industry?.id, custom_context: customContext, mode: convoMode, ...params() });
      setCurrentId(data.session_id);
      onSessionChange?.(data.session_id);
      setAiConnected(data.ai_connected);
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("tmp-")), data.user_message, data.assistant_message]);
      loadSessions();
      checkReadiness(data.session_id);
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
    } finally { setSending(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/assistant/sessions/${id}`, { params: params() });
      if (id === currentId) newChat();
      loadSessions();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const showBanner = aiConnected === false;
  const qsSetup = industry ? (QUICK_SETUP[industry.id] || QUICK_SETUP.custom) : null;
  const qsPopular = industry ? (QS_POPULAR[industry.id] || {}) : {};
  const qsMoodOptions = industry ? (MOOD_BY_INDUSTRY[CONCIERGE_MOOD_KEY[industry.id]] || []) : [];
  const qsIndustryLabel = () => (qsSetup?.industryLabel || (industry?.id === "custom" ? (brief.industry || brief.businessType || "") : industry?.label));
  const buildRecommended = () => {
    const pop = QS_POPULAR[industry.id] || {};
    const reco = QS_RECO[industry.id] || {};
    return {
      industryId: industry.id, industryLabel: qsIndustryLabel(),
      goals: pop.goals || [], pages: pop.pages || [], features: pop.features || [],
      visualStyle: reco.vibe || [], brandPersonality: reco.personality || [], mood: reco.mood || [], visualPref: reco.pref || [],
      budget: qs.budget, timeline: qs.timeline, description: qs.description,
    };
  };

  return (
    <div className={compact ? "flex flex-col h-full min-w-0" : "grid lg:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-13rem)] min-h-[520px]"} data-testid="ai-assistant">
      {/* History sidebar */}
      {!compact && (
      <aside className="hidden lg:flex flex-col bg-navy-800 border border-white/10">
        <button onClick={newChat} data-testid="assistant-new-chat"
          className="m-3 flex items-center justify-center gap-2 bg-gold text-navy py-2.5 font-semibold hover:bg-gold-light transition-colors">
          <Plus size={16} /> New chat
        </button>
        <div className="px-4 pb-2 text-[11px] uppercase tracking-[0.2em] text-gray-500">History</div>
        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1" data-testid="assistant-history">
          {sessions.length === 0 && <div className="px-3 py-4 text-sm text-gray-600">No conversations yet.</div>}
          {sessions.map((s) => (
            <div key={s.id} onClick={() => openSession(s.id)} data-testid={`assistant-session-${s.id}`}
              className={`group flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm transition-colors ${currentId === s.id ? "bg-gold/15 text-gold" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <MessageSquare size={14} className="shrink-0" />
              <span className="flex-1 truncate">{s.title || "Untitled"}</span>
              <button onClick={(e) => remove(s.id, e)} data-testid={`assistant-delete-${s.id}`} aria-label="Delete conversation"
                className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-opacity"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </aside>
      )}

      {/* Chat panel */}
      <section className="flex flex-col bg-navy-800 border border-white/10 min-h-0 min-w-0 flex-1">
        {showBanner && (
          <div className="flex items-start gap-2 bg-gold/10 border-b border-gold/20 px-4 py-2.5 text-xs text-gold" data-testid="assistant-fallback-banner">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>The AI engine had a brief hiccup, so this reply is a friendly placeholder. Your conversation is saved — please try again in a moment.</span>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5" data-testid="assistant-messages">
          {messages.length === 0 && !sending && (
            <div className={`h-full flex flex-col items-center text-center px-4 ${compact ? "justify-start pt-4 pb-2" : "justify-center"}`} data-testid="assistant-empty">
              <div className="w-14 h-14 border border-gold/40 flex items-center justify-center text-gold mb-5"><Bot size={26} strokeWidth={1.5} /></div>
              {!industry ? (
                <>
                  <h3 className="font-heading text-2xl text-white mb-2">What best describes your business or organization?</h3>
                  <p className="text-gray-400 max-w-md mb-8 text-sm">Pick what fits and Top Foster, your AI guide, will tailor everything — your plan, copy, SEO and structure — right to your world.</p>
                  <div className={`grid gap-2.5 max-w-2xl w-full ${compact ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`} data-testid="industry-picker">
                    {INDUSTRIES.map((ind) => {
                      const Ico = ind.icon;
                      return (
                        <button key={ind.id} onClick={() => { setIndustry(ind); onIndustryChange?.(ind.id); seedQsPopular(ind.id); }} data-testid={`industry-${ind.id}`}
                          className="flex items-center gap-2.5 border border-white/15 text-gray-200 px-4 py-3 text-sm hover:border-gold/50 hover:text-white hover:bg-white/5 transition-colors text-left min-w-0">
                          <Ico size={18} className="text-gold shrink-0" /> <span className="min-w-0 leading-tight">{ind.label}</span>
                        </button>
                      );
                    })}
                    <button onClick={() => { setIndustry(CUSTOM_INDUSTRY); onIndustryChange?.("custom"); seedQsPopular("custom"); }} data-testid="industry-custom"
                      className={`flex items-center justify-center gap-2.5 border border-dashed border-gold/40 text-gold px-4 py-3 text-sm hover:bg-gold/10 hover:border-gold transition-colors ${compact ? "col-span-2" : "col-span-2 sm:col-span-3"}`}>
                      <Wand2 size={18} className="shrink-0" /> Other / Custom Business — don't see your industry?
                    </button>
                  </div>
                </>
              ) : industry.id === "custom" && !customContext ? (
                <div className="w-full max-w-lg text-left" data-testid="custom-brief">
                  <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-2 justify-center">
                    <Wand2 size={15} /> Custom Business
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-2 text-center">Tell me about your business</h3>
                  <p className="text-gray-400 mb-6 text-sm text-center">A quick brief lets me tailor every suggestion to you — no matter your niche.</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Business type</label>
                      <input value={brief.businessType} onChange={(e) => setBrief((b) => ({ ...b, businessType: e.target.value }))} data-testid="brief-business-type"
                        placeholder="e.g. Mobile pet grooming, HVAC, winery, dentist…"
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Industry / niche <span className="text-gray-600">(optional)</span></label>
                      <input value={brief.industry} onChange={(e) => setBrief((b) => ({ ...b, industry: e.target.value }))} data-testid="brief-industry"
                        placeholder="e.g. Home services, hospitality, healthcare…"
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Target audience <span className="text-gray-600">(optional)</span></label>
                      <input value={brief.audience} onChange={(e) => setBrief((b) => ({ ...b, audience: e.target.value }))} data-testid="brief-audience"
                        placeholder="Who are your ideal customers?"
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Main goal <span className="text-gray-600">(optional)</span></label>
                      <input value={brief.goals} onChange={(e) => setBrief((b) => ({ ...b, goals: e.target.value }))} data-testid="brief-goals"
                        placeholder="e.g. Get booking requests, sell online, build trust…"
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5">
                    <button onClick={() => { setIndustry(null); onIndustryChange?.(null); }} data-testid="brief-back" className="flex items-center gap-1.5 text-gray-500 hover:text-gold text-xs transition-colors">
                      <ArrowLeft size={13} /> Back
                    </button>
                    <button onClick={submitBrief} data-testid="brief-submit"
                      className="flex items-center gap-2 bg-gold text-navy px-5 py-2.5 font-semibold hover:bg-gold-light transition-colors">
                      <Sparkles size={15} /> Start chatting
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-full max-w-2xl text-left" data-testid="industry-selected-panel">
                  <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-3" data-testid="selected-industry">
                    <industry.icon size={15} /> {industry.label}
                  </div>

                  {hasQuickSetup && (
                    <div className="flex gap-1 mb-5 border border-white/10 p-1 bg-navy w-full max-w-sm" data-testid="concierge-mode-tabs">
                      <button onClick={() => setMode("quick")} data-testid="mode-quick"
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${mode === "quick" ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
                        <Zap size={14} /> Quick setup
                      </button>
                      <button onClick={() => setMode("chat")} data-testid="mode-chat"
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${mode === "chat" ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
                        <MessageSquare size={14} /> Chat with AI
                      </button>
                    </div>
                  )}

                  {hasQuickSetup && mode === "quick" ? (
                    qsPreview ? (
                      <div className="space-y-4" data-testid="qs-preview">
                        <div>
                          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.18em] mb-1"><Sparkles size={13} /> Ready to go</div>
                          <h3 className="font-heading text-xl text-white mb-1">Here's what I'll fill in</h3>
                          <p className="text-gray-400 text-sm">Review your picks — I'll drop these into your assessment and you can change anything after.</p>
                        </div>
                        <dl className="space-y-2.5">
                          {[["Goals", qs.goals], ["Pages", qs.pages], ["Features", qs.features], ["Design vibe", qs.visualStyle], ["Personality", qs.brandPersonality], ["Mood", qs.mood], ["Layout", qs.visualPref],
                            ["Budget", qs.budget ? [qs.budget] : []], ["Timeline", qs.timeline ? [qs.timeline] : []]].filter(([, v]) => v && v.length).map(([label, vals]) => (
                            <div key={label} className="flex gap-3" data-testid={`qs-preview-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                              <dt className="w-24 shrink-0 text-xs uppercase tracking-[0.14em] text-gray-500 pt-1">{label}</dt>
                              <dd className="flex flex-wrap gap-1.5">
                                {vals.map((v) => <span key={v} className="bg-gold/15 text-gold border border-gold/30 px-2.5 py-1 text-xs">{v}</span>)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        {qs.description.trim() && (
                          <div className="flex items-start gap-2 bg-white/[0.04] border border-white/10 px-3 py-2.5 text-xs text-gray-300" data-testid="qs-preview-desc-note">
                            <Wand2 size={14} className="text-gold mt-0.5 shrink-0" />
                            <span>I'll also read your description to fill in your <b className="text-white">business name, mission and who you serve</b> automatically.</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-3 pt-1">
                          <button onClick={() => setQsPreview(false)} data-testid="qs-preview-edit" className="flex items-center gap-1.5 text-gray-400 hover:text-gold text-sm transition-colors shrink-0">
                            <ArrowLeft size={14} /> Edit
                          </button>
                          <button
                            onClick={() => onQuickSetup({
                              industryId: industry.id,
                              industryLabel: qsIndustryLabel(),
                              goals: qs.goals, pages: qs.pages, features: qs.features, visualStyle: qs.visualStyle, brandPersonality: qs.brandPersonality,
                              mood: qs.mood, visualPref: qs.visualPref,
                              budget: qs.budget, timeline: qs.timeline, description: qs.description,
                            })}
                            disabled={quickSetupBusy} data-testid="qs-confirm"
                            className="flex items-center gap-2 bg-gold text-navy px-5 py-2.5 font-semibold hover:bg-gold-light transition-colors disabled:opacity-40">
                            {quickSetupBusy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Confirm &amp; fill my assessment
                          </button>
                        </div>
                      </div>
                    ) : (
                    <div className="space-y-5" data-testid="quick-setup">
                      <div>
                        <h3 className="font-heading text-xl text-white mb-1">Let's build your {industry.label.toLowerCase()} site — fast</h3>
                        <p className="text-gray-400 text-sm">Tap everything that fits — <Star size={11} className="inline -mt-0.5 text-gold fill-gold" /> marks popular picks. I'll drop it straight into your assessment; change anything after.</p>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-gold/[0.07] border border-gold/25 px-3.5 py-3" data-testid="qs-reco-banner">
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-semibold flex items-center gap-1.5"><Zap size={14} className="text-gold" /> Fastest path</div>
                          <div className="text-gray-400 text-xs">Confirm our recommended {industry.label.toLowerCase()} setup in one tap — or customize below.</div>
                        </div>
                        <button onClick={() => onQuickSetup(buildRecommended())} disabled={quickSetupBusy} data-testid="qs-use-recommended"
                          className="flex items-center justify-center gap-2 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-40 shrink-0">
                          {quickSetupBusy ? <Loader2 className="animate-spin" size={14} /> : <Rocket size={14} />} Use recommended setup
                        </button>
                      </div>
                      {[["goals", "What do you want your site to do?"], ["pages", "Which pages do you need?"], ["features", "Which features matter?"]].map(([key, label]) => (
                        <div key={key} data-testid={`qs-group-${key}`}>
                          <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">{label}</div>
                          <div className="flex flex-wrap gap-2">
                            {(qsSetup[key] || []).map((o) => {
                              const on = qs[key].includes(o);
                              const pop = (qsPopular[key] || []).includes(o);
                              return (
                                <button key={o} onClick={() => toggleQs(key, o)} data-testid={`qs-${key}-${o.slice(0, 16).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                  className={`px-3 py-1.5 text-sm border transition-colors ${on ? "bg-gold text-navy border-gold" : `text-gray-300 hover:border-gold/50 hover:text-white ${pop ? "border-gold/40 bg-gold/[0.06]" : "border-white/15"}`}`}>
                                  {on ? <Check size={13} className="inline mr-1 -mt-0.5" /> : (pop && <Star size={11} className="inline mr-1 -mt-0.5 text-gold fill-gold" />)}{o}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <div data-testid="qs-group-visualstyle">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">Design vibe <span className="text-gray-600 normal-case tracking-normal">(pick a few — sets your design style)</span></div>
                        <div className="flex flex-wrap gap-2">
                          {VISUAL_STYLE_OPTIONS.map((o) => {
                            const on = qs.visualStyle.includes(o);
                            return (
                              <button key={o} onClick={() => toggleQs("visualStyle", o)} data-testid={`qs-visualstyle-${o.slice(0, 16).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border transition-colors ${on ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}>
                                <span className="inline-block w-3.5 h-3.5 rounded-sm border border-white/25 shrink-0" style={{ background: VIBE_SWATCH[o] || "#333" }} />
                                {on && <Check size={13} className="-mt-0.5" />}{o}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div data-testid="qs-group-personality">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">Brand personality <span className="text-gray-600 normal-case tracking-normal">(how should it feel?)</span></div>
                        <div className="flex flex-wrap gap-2">
                          {BRAND_PERSONALITY_OPTIONS.map((o) => {
                            const on = qs.brandPersonality.includes(o);
                            return (
                              <button key={o} onClick={() => toggleQs("brandPersonality", o)} data-testid={`qs-personality-${o.slice(0, 16).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                className={`px-3 py-1.5 text-sm border transition-colors ${on ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}>
                                {on && <Check size={13} className="inline mr-1 -mt-0.5" />}{o}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {qsMoodOptions.length > 0 && (
                        <div data-testid="qs-group-mood">
                          <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">{industry.label} mood <span className="text-gray-600 normal-case tracking-normal">(what should it evoke?)</span></div>
                          <div className="flex flex-wrap gap-2">
                            {qsMoodOptions.map((o) => {
                              const on = qs.mood.includes(o);
                              return (
                                <button key={o} onClick={() => toggleQs("mood", o)} data-testid={`qs-mood-${o.slice(0, 16).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                  className={`px-3 py-1.5 text-sm border transition-colors ${on ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}>
                                  {on && <Check size={13} className="inline mr-1 -mt-0.5" />}{o}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div data-testid="qs-group-visualpref">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">Layout preferences <span className="text-gray-600 normal-case tracking-normal">(optional)</span></div>
                        <div className="flex flex-wrap gap-2">
                          {VISUAL_PREF_OPTIONS.map((o) => {
                            const on = qs.visualPref.includes(o);
                            return (
                              <button key={o} onClick={() => toggleQs("visualPref", o)} data-testid={`qs-visualpref-${o.slice(0, 16).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                className={`px-3 py-1.5 text-sm border transition-colors ${on ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}>
                                {on && <Check size={13} className="inline mr-1 -mt-0.5" />}{o}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div data-testid="qs-group-budget">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">Budget range <span className="text-gray-600 normal-case tracking-normal">(optional)</span></div>
                        <div className="flex flex-wrap gap-2">
                          {QS_BUDGETS.map((b) => (
                            <button key={b} onClick={() => setQsField("budget", b)} data-testid={`qs-budget-${b.slice(0, 8).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              className={`px-3 py-1.5 text-sm border transition-colors ${qs.budget === b ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}>{b}</button>
                          ))}
                        </div>
                      </div>
                      <div data-testid="qs-group-timeline">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">Timeline <span className="text-gray-600 normal-case tracking-normal">(optional)</span></div>
                        <div className="flex flex-wrap gap-2">
                          {QS_TIMELINES.map((t) => (
                            <button key={t} onClick={() => setQsField("timeline", t)} data-testid={`qs-timeline-${t.slice(0, 6).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              className={`px-3 py-1.5 text-sm border transition-colors ${qs.timeline === t ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}>{t}</button>
                          ))}
                        </div>
                      </div>
                      <div data-testid="qs-group-desc">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-2">Tell me about your business <span className="text-gray-600 normal-case tracking-normal">(name, mission, who you serve)</span></div>
                        <textarea value={qs.description} onChange={(e) => setQs((s) => ({ ...s, description: e.target.value }))} data-testid="qs-description" rows={3}
                          placeholder="e.g. Grace Community Church — a welcoming, Christ-centered church in Troy, AL serving families and first-time visitors…"
                          className="w-full resize-none bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <p className="text-[11px] text-gray-500 mt-1.5">I'll read this to fill in your mission, audience and descriptive fields automatically.</p>
                      </div>
                      <div className="flex items-center justify-between gap-3 pt-1">
                        <button onClick={() => { setIndustry(null); setCustomContext(null); onIndustryChange?.(null); resetQs(); }} data-testid="change-industry" className="flex items-center gap-1.5 text-gray-500 hover:text-gold text-xs transition-colors shrink-0">
                          <ArrowLeft size={13} /> Change industry
                        </button>
                        <button
                          onClick={() => setQsPreview(true)}
                          disabled={qs.goals.length + qs.pages.length + qs.features.length + qs.visualStyle.length + qs.brandPersonality.length + qs.mood.length + qs.visualPref.length === 0 && !qs.description.trim()}
                          data-testid="qs-build"
                          className="flex items-center gap-2 bg-gold text-navy px-5 py-2.5 font-semibold hover:bg-gold-light transition-colors disabled:opacity-40">
                          <Wand2 size={15} /> Review &amp; build
                        </button>
                      </div>
                    </div>
                    )
                  ) : (
                    <div className="text-center">
                      <h3 className="font-heading text-2xl text-white mb-2">Let's build your {industry.label.toLowerCase()} website</h3>
                      <p className="text-gray-400 max-w-md mb-6 text-sm mx-auto">Start with a prompt below, or type your own question.</p>
                      <div className="flex flex-wrap justify-center gap-2 max-w-xl mx-auto" data-testid="assistant-quick-prompts">
                        {industry.chips.map((p) => (
                          <button key={p} onClick={() => send(p)} data-testid={`assistant-chip-${p.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                            className="flex items-center gap-1.5 border border-white/15 text-gray-300 px-3.5 py-2 text-sm hover:border-gold/50 hover:text-white transition-colors">
                            <Sparkles size={13} className="text-gold" /> {p}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => { setIndustry(null); setCustomContext(null); onIndustryChange?.(null); resetQs(); }} data-testid="change-industry" className="mt-6 inline-flex items-center gap-1.5 text-gray-500 hover:text-gold text-xs transition-colors">
                        <ArrowLeft size={13} /> Change industry
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`assistant-msg-${m.role}`}>
                <div className={`w-8 h-8 shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-gold text-navy" : "border border-gold/40 text-gold"}`}>
                  {m.role === "user" ? <User size={15} /> : <Bot size={15} />}
                </div>
                <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-gold text-navy whitespace-pre-wrap" : "bg-navy border border-white/10 text-gray-200"}`}>
                  {m.role === "user" ? m.content : (
                    <div className="md-body break-words"><ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{m.content}</ReactMarkdown></div>
                  )}
                  {m.role === "assistant" && m.ai_connected === false && (
                    <span className="block mt-2 text-[10px] uppercase tracking-wide text-gold/70">Placeholder reply</span>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {sending && (
            <div className="flex gap-3" data-testid="assistant-loading">
              <div className="w-8 h-8 shrink-0 flex items-center justify-center border border-gold/40 text-gold"><Bot size={15} /></div>
              <div className="bg-navy border border-white/10 px-4 py-3.5"><TypingDots /></div>
            </div>
          )}
        </div>

        {/* Conversation readiness → contextual next-step bridge (only when ready) */}
        {!compact && readiness?.ready && (
          <div className="border-t border-gold/25 bg-gold/[0.06] px-4 py-3" data-testid="conversation-readiness">
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.2em] mb-2">
                  <Sparkles size={12} /> Project Readiness
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-white font-heading text-2xl leading-none" data-testid="readiness-score">{readiness.score}%</div>
                  <div className="text-gray-400 text-xs">website intent</div>
                  <div className="flex-1 max-w-[180px] h-1.5 bg-white/10 overflow-hidden rounded-full">
                    <motion.div className="h-full bg-gold" initial={{ width: 0 }} animate={{ width: `${readiness.score}%` }} transition={{ duration: 0.5 }} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {[["business_identified", "Business"], ["audience", "Audience"], ["goal", "Goal"], ["pages", "Pages"]].map(([k, label]) => {
                    const ok = readiness.signals?.[k];
                    return (
                      <span key={k} className={`flex items-center gap-1.5 text-xs ${ok ? "text-gray-200" : "text-gray-500"}`} data-testid={`readiness-signal-${k}`}>
                        {ok ? <CheckCircle2 size={13} className="text-gold" /> : <MinusCircle size={13} className="text-gray-600" />}
                        {label} <b className={ok ? "text-gold" : "text-gray-600"}>{ok ? "YES" : "—"}</b>
                      </span>
                    );
                  })}
                </div>
              </div>
              {(() => { const Ico = ACTION_ICON[readiness.recommendation?.action] || Rocket; return (
                <button onClick={goToRecommendation} data-testid="readiness-cta"
                  className="flex items-center gap-2 bg-gold text-navy px-5 py-3 font-semibold hover:bg-gold-light transition-colors shrink-0">
                  <Ico size={16} /> {readiness.recommendation?.label} <ArrowRight size={15} />
                </button>
              ); })()}
            </div>
          </div>
        )}

        {/* Input — hidden while the guided Quick Setup panel is showing (its own description box + build CTA replace it) */}
        {!(hasQuickSetup && mode === "quick" && industry && messages.length === 0) && (
        <div className="border-t border-white/10 p-3 md:p-4">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input} onChange={(e) => setInput(e.target.value)} rows={1} data-testid="assistant-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={scope === "admin" ? "Ask me anything…" : "Tell Top Foster about your business or organization in your own words…"}
              className="flex-1 resize-none bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none max-h-40 overflow-y-auto" />
            <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="assistant-send"
              className="h-[46px] px-5 bg-gold text-navy font-semibold hover:bg-gold-light transition-colors flex items-center gap-2 disabled:opacity-50">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            </button>
          </div>
        </div>
        )}
      </section>
    </div>
  );
};
